import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Activity, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password/reset", { token, new_password: form.password });
      setDone(true);
      toast.success("Password reset. Redirecting to sign in…");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md bg-[#0B5FFF] text-white flex items-center justify-center">
            <Activity className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-[#111827]">RapidCare SA</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">Choose a new password</div>
          </div>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            {!token ? (
              <div className="text-center py-8" data-testid="reset-missing-token">
                <h1 className="font-heading text-xl font-bold text-[#111827]">Invalid link</h1>
                <p className="text-sm text-slate-600 mt-2">This reset link is missing a token. Request a new one.</p>
                <Link to="/forgot-password">
                  <Button className="mt-6 w-full bg-[#0B5FFF] hover:bg-[#0A54E0] h-11">Request new link</Button>
                </Link>
              </div>
            ) : done ? (
              <div className="text-center py-6" data-testid="reset-success-state">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-[#111827]">Password reset!</h1>
                <p className="text-sm text-slate-600 mt-2">Redirecting you to sign in…</p>
              </div>
            ) : (
              <>
                <h1 className="font-heading text-2xl font-bold text-[#111827]">Set a new password</h1>
                <p className="text-sm text-slate-600 mt-2">Choose something at least 6 characters long.</p>
                <form onSubmit={submit} className="space-y-4 mt-6">
                  <div>
                    <Label htmlFor="password">New password</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={6}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="mt-1.5"
                      data-testid="reset-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm">Confirm password</Label>
                    <Input
                      id="confirm"
                      type="password"
                      required
                      minLength={6}
                      value={form.confirm}
                      onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                      className="mt-1.5"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#0B5FFF] hover:bg-[#0A54E0] h-11" data-testid="reset-submit">
                    {loading ? "Saving..." : "Reset password"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
